<script>
import { reactive, toRefs, watchEffect, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { modifyPassword } from "@/api/modules/user"
import { validatorComp } from '@/compositions'
import { encryptUtils, vab, commonUtils } from "@libs/utils"
import { useI18n } from '@/plugins/i18n'
import SrUpload from "@/components/SrUpload"
import {
  addAgreementFile,
  addBankAcct,
  getAgreementInfo,
  getBankAcct,
  updateBankAcct,
} from "@/api/modules/auth"
import fileComp from "@/compositions/modules/file"
import {
  CodeToText,
  provinceAndCityData,
  TextToCode,
} from "element-china-area-data"
import { getRegisterInfo } from "@/utils/common-req"

const { throttle } = commonUtils

export default {
  components: { SrUpload },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const { FILE } = store.state.base.constant
    store.dispatch('user/getUserInfo')

    // 初始化注册信息
    getRegisterInfo()

    const state = reactive({
      userInfo: store.state.user.userInfo,
      userId: store.state.user.userId,
      cardBgGray: require('@/assets/img/dashboard/card-gray.png'),
    })
    const showPassword = reactive({
      oldPasswordVisible: false,
      newPasswordVisible: false,
      passwordAgainVisible: false
    })
    const refs = reactive({ formRef: null })

    // 初始化form
    const { formReq, rules, loadingScope } = initForm()

    // 初始化协议管理模块
    const _initProtocol = initProtocol()

    // 初始化银行卡管理模块
    const _initCard = initCard()

    // 初始化标题处理
    const _initTitle = initTitle()

    const scopedComputed = {
      countryTemp: computed(() => store.state.base.countryTemp),
      curTemp: computed(() => {
        let options = []
        let mappings = {}
        for (let item in store.state.base.curTemp) {
          mappings[item] = item
          if (!(item === "CNY" || item === "CNH")) {
            options.push({ label: item, value: item })
          }
        }
        return { options, mappings }
      }),
    }

    watchEffect(() => {
      state.userInfo = store.state.user.userInfo
    })

    // 重置密码
    const resetPassword = () => {
      refs.formRef.validate(async valid => {
        if (valid) {
          loadingScope.btnLoading = true
          const { oldPassword, newPassword } = formReq
          try {
            await modifyPassword({ oldPassword: encryptUtils.encrypt(oldPassword), newPassword: encryptUtils.encrypt(newPassword) })
            vab.baseMessage(t('global.content.text_2'), 'success')
          } catch (err) {
            loadingScope.btnLoading = false
            console.log(err)
          }
        }
      })
    }

    watch(
      () => _initProtocol.protocolForm.agreementFileId,
      async (value) => {
        if (value) {
          try {
            const params = {
              agreementFileId: _initProtocol.protocolForm.agreementFileId,
            }
            await addAgreementFile(params)
            vab.baseMessage(t('global.content.text_3'), 'success')
            await getRegisterInfo()
            _initProtocol.fetchAgreementInfo()
          } catch (err) {
            vab.baseMessage(t('global.content.text_4'), 'error')
          }
        }
      }
    )

    return {
      t,
      formReq,
      resetPassword,
      state,
      rules,
      ...toRefs(loadingScope),
      ...toRefs(refs),
      ...toRefs(showPassword),
      ...toRefs(_initProtocol),
      ...toRefs(_initCard),
      ...toRefs(_initTitle),
      ...scopedComputed,
      FILE,
      provinceAndCityData
    }
  }
}

const initForm = () => {
  const { validateConfirmPwd } = validatorComp()
  const { t } = useI18n()
  const formReq = reactive({
    newPassword: "",
    oldPassword: "",
    passwordAgain: "",
  })
  // loading
  const loadingScope = reactive({
    btnLoading: false
  })
  const rules = reactive({
    oldPassword: [{ required: true, message: t('user.setting.placeholder.text_1'), trigger: 'change' }],
    newPassword: [{ required: true, message: t('user.setting.placeholder.text_2'), trigger: 'change' }],
    passwordAgain: [
      { required: true, message: t('user.register.placeholder.text_5'), trigger: 'blur' },
      { validator: (rule, value, callback) => validateConfirmPwd(rule, value, callback, { password: formReq.newPassword }), trigger: 'blur' }
    ]
  })
  return { formReq, rules, loadingScope }
}

// 初始化协议管理模块
const initProtocol = () => {
  const { getFileRemoteInfo, template } = fileComp()
  const { t } = useI18n()
  const protocolForm = reactive({
    agreementFileId: "",
    agreementFileName: "",
    agreementRejectRemark: null,
    agreementCheckStatus: "",
  })
  const state = reactive({
    serviceProtocol: template.serviceProtocol,
    bizIntAgreement: template.bizIntAgreement,

  })
  const store = useStore()
  const kycReviewStatus = computed(() => store.state.auth?.registerInfo?.kycReviewStatus || "")
  const agreementReviewStatus = computed(() => store.state.auth?.registerInfo?.agreementReviewStatus || "")


  const scopedMethods = {
    showToastBeforeUpload(status) {
      if (status !== "CF") {
        vab.baseMessage(t("dashboard.step.other.text_1"), "error");
      }
    },
    // 下载
    downFile: (url, fileName) => {
      const link = document.createElement("a")
      link.href = url
      link.download = fileName
      link.target = "_blank"
      link.style.display = "none"
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(link.href)
      document.body.removeChild(link)
    },
    fetchAgreementInfo: async () => {
      console.log("fetchAgreementInfo")
      const res = await getAgreementInfo()

      protocolForm.agreementRejectRemark = res.agreementRejectRemark
      protocolForm.agreementCheckStatus = res.agreementCheckStatus
      return res
    },
    // 查看已上传协议
    handleViewPact: () => {
      const fetchProtocol = async () => {
        const res = await scopedMethods.fetchAgreementInfo()
        const { agreementFileId } = res
        const { fileName, url } = await getFileRemoteInfo(agreementFileId)
        scopedMethods.downFile(url, fileName)
      }
      fetchProtocol()
    },
    // 下载协议信息 - 参考sr-header实现
    downloadAgreementInfo: async () => {
      try {
        const res = await scopedMethods.fetchAgreementInfo()
        if (res.agreementCheckStatus !== 'CF') return
        const { agreementFileId } = res
        const { fileName, url } = await getFileRemoteInfo(agreementFileId)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.target = '_blank'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(link.href)
        document.body.removeChild(link)
      } catch (err) {
        console.log(`%c ${'当前输出关注'}`, 'color:#f00', err)
        vab.baseMessage('下载协议失败', 'error')
      }
    },
  }
  scopedMethods.fetchAgreementInfo()
  return { ...toRefs(state), protocolForm, ...scopedMethods, kycReviewStatus, agreementReviewStatus }
}

// 初始化银行卡管理模块
const initCard = () => {
  const {
    validateMobilePhone,
    validateIdentityCard,
    validateUSCC,
    validateBIC,
  } = validatorComp()
  const store = useStore()
  const { t } = useI18n()
  const countryTemp = computed(() => store.state.base.countryTemp)
  const bankAcctCheckStatus = computed(() => store.state.auth?.registerInfo?.bankAcctCheckStatus || "")


  const cardForm = reactive({
    bankCtryCode: "CN", // 地区,CN=大陆,HK=香港
    bankAcctType: "1", // 银行账户类型,1=公司,2=个人
    bankAcctName: "", // 账户名称
    bankAcctNo: "", // 银行账号
    phone: "", // 联系方式
    bankName: "", // 开户行名称
    bankProvince: "", // 银行所在省份
    bankCity: "", // 银行所在城市
    bankProvinceAndCityData: "", // 银行所在省份和城市
    cardId: "", // 身份证号
    cur: "CNY", // 收款币种
    bankAcctCheckStatus: "", // 卡的状态
    id: "", // 卡的状态
  })
  const validateIdCard = (rule, value, callback) => {
    if (!/^[a-zA-Z0-9]*$/.test(value)) {
      callback(new Error(t("global.validate.characterSet")))
    } else {
      callback()
    }
  }
  const state = reactive({
    dialogVisible: false,
    bankAuthStatusEnum: {
      CF: t("dashboard.bank.content.text_3"),
      RJ: t("dashboard.bank.content.text_4"),
      RC: t("dashboard.bank.content.text_5"),
      ST: t("dashboard.bank.content.text_6"),
      CP: t("dashboard.bank.content.text_7"),
      WC: t("dashboard.bank.content.text_8"),
    },
    bankAcctTypeEnum: {
      1: t("dashboard.dialog.placeholder.text_4"),
      2: t("dashboard.dialog.placeholder.text_5"),
    },
    cardFormRef: null,
    loadingBankForm: false,
    viewDialogVisible: false,
    registerInfo: store.state.auth.registerInfo,
    bankList: [],
    cardRules: {
      bankCtryCode: [
        {
          required: true,
          message: t("dashboard.bank.error.text_1"),
          trigger: "change",
        },
      ],
      bankAcctType: [
        {
          required: true,
          message: t("dashboard.bank.error.text_2"),
          trigger: "change",
        },
      ],
      bankAcctName: [
        {
          required: true,
          message: t("dashboard.dialog.placeholder.text_6"),
          trigger: "blur",
        },
      ],
      bankAcctNo: [
        {
          required: true,
          message: t("dashboard.dialog.placeholder.text_7"),
          trigger: "blur",
        },
        { validator: validateIdCard, trigger: "blur" },
      ],
      phone: [
        {
          required: true,
          message: t("user.register.placeholder.text_3"),
          trigger: "blur",
        },
        { validator: validateMobilePhone, trigger: "blur" },
      ],
      bankName: [
        {
          required: true,
          message: t("dashboard.dialog.placeholder.text_10"),
          trigger: "blur",
        },
      ],
      bankProvinceAndCityData: [
        {
          required: true,
          message: t("dashboard.bank.error.text_3"),
          trigger: "change",
        },
      ],
      cur: [
        {
          required: true,
          message: t("dashboard.bank.error.text_4"),
          trigger: "change",
        },
      ],
      cardId: [
        {
          required: true,
          message: t("dashboard.bank.error.text_5"),
          trigger: "blur",
        },
        { validator: validateIdentityCard, trigger: "blur" },
      ],
      bankCity: [
        {
          required: true,
          message: t("dashboard.bank.error.text_6"),
          trigger: "blur",
        },
      ],
    },
  })
  const scopedMethods = {
    // 处理卡号显示 - 参考operation-control实现
    handleCardId: (str) => ('' + str).replace(/(\d{4}).+(\d{4})/, '$1 ****** $2'),
    // 添加银行卡
    handleAdd: () => {
      // if (!scopedMethods.cardClickable()) {
      //   return vab.baseMessage(t("dashboard.step.other.text_1"), "error")
      // }
      state.dialogVisible = true
    },
    initBankData: async () => {
      const res = await getBankAcct()
      state.bankList = res?.list || []
    },
    handleViewCard: async () => {
      await scopedMethods.initBankData()
      state.viewDialogVisible = true
    },
    cardClickable: () => {
      return store.state.auth.registerInfo.agreementReviewStatus === "CF"
    },
    changeCtryCode: (val) => {
      if (val === "CN") {
        cardForm.cur = "CNY"
      } else {
        cardForm.cur = "CNH"
      }
    },
    // 提交银行卡
    handleSubmitCardForm: throttle(() => {
      state.cardFormRef.validate(async (valid) => {
        if (valid) {
          const {
            id,
            bankCtryCode,
            bankAcctType,
            bankAcctName,
            bankProvince,
            bankAcctNo,
            phone,
            bankName,
            bankCity,
            bankProvinceAndCityData,
            cardId,
            cur,
            bankAcctCheckStatus = "",
          } = cardForm
          let params = {
            bankCtryCode,
            bankAcctType,
            bankAcctName,
            bankAcctNo,
            phone,
            bankName,
            cur,
          }
          if (cardForm.bankCtryCode === "CN") {
            if (cardForm.bankAcctType === "1") {
              params.bankProvince = CodeToText[bankProvinceAndCityData[0]]
              params.bankCity = CodeToText[bankProvinceAndCityData[1]]
            }
          } else {
            params.bankProvince =
              countryTemp.value.find(
                (item) => item.id === cardForm.bankCtryCode
              )?.cname || ""
            params.bankCity = bankCity
          }

          if (cardForm.bankCtryCode === "CN") {
            if (cardForm.bankAcctType === "2") {
              params.cardId = cardId
            }
          }
          if (params.bankCtryCode === "CN") {
            if (params.cur == "CNH") {
              params.cur = "CNY"
            }
          } else {
            if (params.cur == "CNY") {
              params.cur = "CNH"
            }
          }
          try {
            if (["RJ", "RC", "ST"].includes(bankAcctCheckStatus)) {
              await updateBankAcct({ ...params, id })
              vab.baseMessage(t("global.content.text_5"), "success")
            } else {
              await addBankAcct(params)
              vab.baseMessage(t("global.content.text_6"), "success")
            }
            await getRegisterInfo()
            await scopedMethods.initBankData()
            state.dialogVisible = false
          } catch {
            // state.dialogVisible = false
          }
        }
      })
    }, 1000),
    handleEditCard: async () => {
      state.loadingBankForm = true
      state.dialogVisible = true
      try {
        const { list } = await getBankAcct()
        if (!list.length) throw new Error(t("dashboard.bank.error.text_7"))
        cardForm.bankCtryCode = list[0].bankCtryCode
        cardForm.bankAcctType = list[0].bankAcctType
        cardForm.bankAcctName = list[0].bankAcctName
        cardForm.bankAcctNo = list[0].bankAcctNo
        cardForm.phone = list[0].phone
        cardForm.bankName = list[0].bankName

        if (list[0].bankCtryCode === "CN" || list[0].bankCtryCode === "TW") {
          cardForm.bankProvince = ""
          cardForm.bankCity = ""
          cardForm.bankProvince = ""
        } else {
          cardForm.bankProvince = list[0].bankProvince
          cardForm.bankCity = list[0].bankCity
          cardForm.bankProvince = list[0].bankProvince
        }
        if (+list[0].bankAcctType !== 2) {
          if (list[0].bankCtryCode === "CN" || list[0].bankCtryCode === "TW") {
            cardForm.bankProvinceAndCityData = [
              TextToCode[list[0].bankProvince].code,
              TextToCode[list[0].bankProvince][list[0].bankCity].code,
            ]
          } else {
            cardForm.bankProvinceAndCityData = []
          }
        }
        cardForm.cardId = list[0].cardId
        cardForm.cur = list[0].cur
        cardForm.id = list[0].id
        cardForm.bankAcctCheckStatus = list[0].bankAcctCheckStatus
        state.loadingBankForm = false
      } catch {
        state.loadingBankForm = false
      }
    },
  }
  scopedMethods.initBankData()
  return { ...toRefs(state), cardForm, ...scopedMethods, bankAcctCheckStatus }
}

// 初始化标题处理
const initTitle = () => {
  const { t } = useI18n()
  const scopedMethods = {
    handleTitle: (item) => {
      const titleEnum = {
        kycReviewStatus: {
          title: {
            WC: t("dashboard.step.title.text_1"),
            CP: t("dashboard.step.title.text_2"),
            CF: t("dashboard.step.title.text_3"),
            RJ: t("dashboard.step.title.text_4"),
            RC: t("dashboard.step.title.text_2"),
            ST: t("dashboard.step.title.text_4"),
          },
        },
        agreementReviewStatus: {
          title: {
            WC: t("dashboard.step.title.text_5"),
            CP: t("dashboard.step.title.text_6"),
            CF: t("dashboard.step.title.text_7"),
            RJ: t("dashboard.step.title.text_8"),
            RC: t("dashboard.step.title.text_6"),
            ST: t("dashboard.step.title.text_8"),
          },
        },
        bankAcctCheckStatus: {
          title: {
            WC: t("dashboard.step.title.text_9"),
            CP: t("dashboard.step.title.text_10"),
            CF: t("dashboard.step.title.text_11"),
            RJ: t("dashboard.step.title.text_12"),
            RC: t("dashboard.step.title.text_10"),
            ST: t("dashboard.step.title.text_12"),
          },
        },
      }
      return (
        titleEnum[item.key].title[item.status] || titleEnum[item.key].title.WC
      )
    },
  }

  return { ...scopedMethods }
}
</script>

<template>
  <div class="personal-container">
    <!--个人信息-->
    <div class="content-wrap">
      <div class="title-wrap">
        <div>{{ t('user.setting.title.text_2') }}</div>
      </div>
      <div class="subtitle-wrap">
        <div>{{ t('user.setting.content.text_2') }}</div>
      </div>

      <div class="form-item-wrap">
        <el-form-item :label="t('user.setting.form.text_5')">
          <div>{{ state.userInfo.mobile }}</div>
        </el-form-item>
        <el-form-item :label="t('user.setting.form.text_6')" style="margin-bottom: 0;">
          <div>{{ state.userInfo.email }}</div>
        </el-form-item>
      </div>
    </div>

    <!--安全信息-->
    <div class="content-wrap">
      <div class="title-wrap">
        <div>{{ t('user.setting.title.text_1') }}</div>
      </div>
      <div class="subtitle-wrap">
        <div>{{ t('user.setting.content.text_1') }}</div>
      </div>
      <div class="form-item-wrap">
        <el-form :model="formReq" :rules="rules" ref="formRef" label-width="150px" label-position="left">
          <el-form-item :label="t('user.setting.form.text_1')">
            <div>{{ state.userId }}</div>
          </el-form-item>
          <el-form-item prop="oldPassword" :label="t('user.setting.form.text_2')">
            <div class="password-wrap">
              <el-input v-model="formReq.oldPassword" :placeholder="t('user.setting.placeholder.text_1')"
                :type="oldPasswordVisible ? 'text' : 'password'" />
              <div class="icon" @click="oldPasswordVisible = !oldPasswordVisible">
                <svg-icon :icon-class="oldPasswordVisible ? '眼睛' : '闭眼睛'" />
              </div>
            </div>
          </el-form-item>
          <el-form-item prop="newPassword" :label="t('user.setting.form.text_3')">
            <div class="password-wrap">
              <el-input v-model="formReq.newPassword" :placeholder="t('user.setting.placeholder.text_2')"
                :type="newPasswordVisible ? 'text' : 'password'" />
              <div class="icon" @click="newPasswordVisible = !newPasswordVisible">
                <svg-icon :icon-class="newPasswordVisible ? '眼睛' : '闭眼睛'" />
              </div>
            </div>
          </el-form-item>
          <el-form-item prop="passwordAgain" :label="t('user.setting.form.text_4')">
            <div class="password-wrap">
              <el-input v-model="formReq.passwordAgain" :placeholder="t('user.setting.placeholder.text_3')"
                :type="passwordAgainVisible ? 'text' : 'password'" />
              <div class="icon" @click="passwordAgainVisible = !passwordAgainVisible">
                <svg-icon :icon-class="passwordAgainVisible ? '眼睛' : '闭眼睛'" />
              </div>
            </div>
          </el-form-item>
          <div class="confirm" @click="resetPassword">{{ t('user.setting.content.text_3') }}</div>
        </el-form>
      </div>
    </div>
    <!--协议管理-->
    <div class="content-wrap">
      <div class="title-wrap">
        <div>{{ t('dashboard.step.title.termsAndPolicies') }}</div>
      </div>
      <div class="subtitle-wrap">
        <div v-if="(['CF'].includes(protocolForm.agreementCheckStatus))">{{ t('dashboard.step.content.text_7') }}</div>
        <div v-else-if="['CP', 'RC'].includes(protocolForm.agreementCheckStatus)">
          {{ t('dashboard.step.content.signAgreementTipCP') }}
        </div>
        <div v-else-if="['RJ', 'ST'].includes(protocolForm.agreementCheckStatus)">
          {{ t('dashboard.step.content.signAgreementTipRJ5') }}
        </div>
        <div v-else>
          <span v-if="localCN">请下载协议模板，签署盖章后上传扫描件</span>
          <span v-else-if="!localCN">Please download sign/stamp,and upload the scanned copy.</span>

        </div>
      </div>
      <div class="form-item-wrap">
        <div v-if="protocolForm.agreementCheckStatus === 'CF'">
          <div style="display: flex;justify-content: space-between;align-items: center;">
            <div style="text-decoration: underline;cursor: pointer;" @click="downloadAgreementInfo">{{
              t('dashboard.step.content.signAgreementTipRJ2') }}</div>
            <div style="color: #209B75;display: flex;align-items: center;">
              <el-icon style="margin-right:5px">
                <CircleCheck />
              </el-icon>
              {{ t('user.setting.content.signedSuccessfully') }}
            </div>
          </div>

        </div>
        <div v-else-if="['CP', 'RC'].includes(protocolForm.agreementCheckStatus)">

          <div style="text-decoration: underline;cursor: pointer;" @click="handleViewPact">
            {{ t('global.content.text_16') }}</div>
        </div>
        <div v-else>
          <div style="display: flex;align-items: center;margin-bottom: 2px;justify-content: space-between;">
            <span v-if="localCN"><a class="a" :href="serviceProtocol" download="《寻汇营销会员合作协议》">{{
              t('dashboard.step.content.signAgreementTip4')
                }}</a></span>
            <span v-else-if="!localCN"><a class="a" :href="bizIntAgreement"
                download="Business Introduction Agreement">&nbsp;{{
                  t('dashboard.step.content.signAgreementTip4') }}&nbsp;</a></span>
            <el-button>
              <sr-upload v-model:fileId="protocolForm.agreementFileId" v-model:fileName="protocolForm.agreementFileName"
                :filePrefix="FILE.PREFIX.AGREEMENT" fileSize="15"
                :mode="kycReviewStatus === 'CF' ? FILE.UPLOAD_MODE.EDITABLE : FILE.UPLOAD_MODE.READONLY">
                <div :class="['a']" @click="showToastBeforeUpload(kycReviewStatus)">
                  {{ t('dashboard.step.content.signAgreementTip3') }}</div>
              </sr-upload>
            </el-button>
          </div>
          <div class="red" v-if="['RJ', 'ST'].includes(protocolForm.agreementCheckStatus)">
            {{ t('dashboard.step.content.text_17') }}:{{ protocolForm.agreementRejectRemark }}
          </div>
        </div>
      </div>
    </div>

    <!-- todo -->
    <!--银行卡管理-->
    <div class="content-wrap">
      <div class="title-wrap">
        <div>{{ t('dashboard.step.title.collectionBankAccount') }}</div>
      </div>
      <div class="subtitle-wrap">

        <div v-if="bankAcctCheckStatus === 'CF'">
          <!--银行卡信息展示 - 参考operation-control实现-->
          <!-- <div>{{ t('dashboard.step.content.text_11') }}</div> -->
        </div>
        <div v-else-if="bankList.length && ['CP', 'RC'].includes(bankList[0].bankAcctCheckStatus)">
          {{ t('dashboard.step.content.text_12') }}
        </div>
        <div v-else-if="bankList.length && ['RJ', 'ST'].includes(bankList[0].bankAcctCheckStatus)">
          {{ t('dashboard.step.content.text_13') }}
        </div>
        <div v-else>
          {{ t('dashboard.step.content.text_13') }}
        </div>
      </div>

      <div class="form-item-wrap" v-if="['CP', 'RC', 'RJ', 'ST'].includes(bankAcctCheckStatus)">

        <div v-if="bankList.length && ['CP', 'RC'].includes(bankList[0].bankAcctCheckStatus)">

          <div class="a" @click="handleViewCard">{{ t('dashboard.step.content.text_15') }}</div>
        </div>
        <div v-else-if="bankList.length && ['RJ', 'ST'].includes(bankList[0].bankAcctCheckStatus)">
          <div :class="['a', cardClickable() ? '' : 'gray']" @click="handleEditCard">{{
            t('dashboard.step.content.text_16') }}
          </div>
          <span class="red" v-if="bankList.length">{{ t('dashboard.step.content.text_17') }}:{{
            bankList[0].rejectRemark }}</span>
        </div>

      </div>

      <div class="form-item-wrap" style="background: #FFF;padding-left: 14px;padding-top: 10px;" v-else>
        <div v-if="bankAcctCheckStatus === 'CF' && bankList.length">
          <!--银行卡信息展示 - 参考operation-control实现-->
          <div class="card-info-wrap">
            <span :class="['card-status', `card-status-${bankList[0].bankAcctCheckStatus}`]">
              <el-icon style="margin-right:5px">
                <CircleCheck />
              </el-icon>
              {{ bankAuthStatusEnum[bankList[0].bankAcctCheckStatus] }}</span>
            <div class="card-h2" style="color: #333;">{{ bankList[0].bankAcctName }}</div>
            <div>
              <div class="card-h1" style="color: #333;">{{ handleCardId(bankList[0].bankAcctNo) }}</div>
              <div style="font-size: 14px;color: #909599;">{{ bankList[0].bankName }}</div>
            </div>
            <div class="df-jc-sb" style="font-size: 13px;color: #444646;margin-top: 10px;">
              <div>{{ t('dashboard.bank.content.text_2') }} {{ bankList[0].cur }}</div>
              <div v-if="bankList[0].bankBic">{{ bankList[0].bankBic }}</div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="add-bank-card" @click="handleAdd" style="">
            <el-icon :size="20">
              <CirclePlus />
            </el-icon>
            <span style="font-size: 14px; margin-top: 10px;">{{ t('dashboard.bank.content.addBankAcct') }}</span>
          </div>
          <!-- {{ t('dashboard.step.content.text_13') }}
          <div class="a" @click="handleAdd">{{ t('dashboard.step.content.text_18') }}</div> -->
        </div>
      </div>
    </div>



    <!--银行卡添加/编辑弹框-->
    <div class="add-card-container">
      <el-dialog custom-class="reset-dialog-container" :width="localCN ? '544px' : '600px'" v-model="dialogVisible"
        @close="dialogVisible = false" top="84px" center>
        <template #title>
          <div class="dialog-header">{{ t('dashboard.dialog.title.text_1') }}</div>
        </template>
        <div class="dialog-body">
          <div class="dialog-topic">
            <div class="dialog-topic-item">{{ t('dashboard.dialog.content.text_1') }}</div>
            <div class="dialog-topic-item">{{ t('dashboard.dialog.content.text_2') }}</div>
          </div>
          <el-form :model="cardForm" :rules="cardRules" ref="cardFormRef" :label-width="localCN ? '160px' : '180px'"
            label-position="right" v-loading="loadingBankForm">
            <el-row>
              <el-form-item prop="bankCtryCode" :label="t('dashboard.dialog.label.text_1')">
                <el-select v-model="cardForm.bankCtryCode" @change="changeCtryCode">
                  <el-option v-for="(val, key) in countryTemp" :key="key" :label="localCN ? val.cname : val.ename"
                    :value="val.id" />
                </el-select>
              </el-form-item>

              <el-form-item prop="bankAcctType" :label="t('dashboard.dialog.label.text_2')">
                <el-radio-group v-model="cardForm.bankAcctType">
                  <el-radio label="1">{{ t('dashboard.dialog.placeholder.text_4') }}</el-radio>
                  <el-radio label="2" v-if="cardForm.bankCtryCode === 'CN' || cardForm.bankCtryCode === 'TW'">{{
                    t('dashboard.dialog.placeholder.text_5') }}</el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item prop="bankAcctName" :label="t('dashboard.dialog.label.text_3')">
                <el-input :placeholder="t('dashboard.dialog.placeholder.text_6')" v-model="cardForm.bankAcctName" />
              </el-form-item>

              <el-form-item prop="cur" :label="t('dashboard.dialog.label.text_4')">
                <el-select v-model="cardForm.cur" :disabled="cardForm.bankCtryCode === 'CN'">
                  <el-option label="CNY" value="CNY" v-if="cardForm.bankCtryCode === 'CN'"></el-option>
                  <el-option label="CNH" value="CNH" v-else></el-option>
                  <el-option v-for="(val, key) in curTemp.options" :key="key" :label="val.label" :value="val.value" />
                </el-select>
              </el-form-item>

              <el-form-item prop="bankAcctNo" :label="t('dashboard.dialog.label.text_5')">
                <el-input :placeholder="t('dashboard.dialog.placeholder.text_7')" v-model="cardForm.bankAcctNo" />
              </el-form-item>

              <el-form-item prop="cardId" :label="t('dashboard.dialog.label.text_7')"
                v-if="cardForm.bankAcctType === '2'">
                <el-input :placeholder="t('dashboard.dialog.placeholder.text_9')" v-model="cardForm.cardId" />
              </el-form-item>

              <el-form-item prop="bankName" :label="t('dashboard.dialog.label.text_8')">
                <el-input :placeholder="t('dashboard.dialog.placeholder.text_10')" v-model="cardForm.bankName" />
              </el-form-item>

              <el-form-item :prop="(cardForm.bankCtryCode === 'CN') ? 'bankProvinceAndCityData' : 'bankCity'"
                :label="t('dashboard.dialog.label.text_9')" v-if="cardForm.bankAcctType === '1'">
                <el-cascader v-if="cardForm.bankCtryCode === 'CN'" size="large" :options="provinceAndCityData"
                  v-model="cardForm.bankProvinceAndCityData" />
                <el-input v-else :placeholder="t('dashboard.dialog.placeholder.text_11')" v-model="cardForm.bankCity" />
              </el-form-item>
            </el-row>
          </el-form>
        </div>
        <template #footer>
          <div class="dialog-footer">
            <el-button type="default" @click="dialogVisible = false">{{ t('dashboard.dialog.content.text_3')
              }}</el-button>
            <el-button type="primary" @click="handleSubmitCardForm">{{ t('dashboard.dialog.content.text_4')
              }}</el-button>
          </div>
        </template>
      </el-dialog>
    </div>

    <!--银行卡查看弹框-->
    <div class="add-card-container">
      <el-dialog custom-class="reset-dialog-container" :width="localCN ? '544px' : '600px'" v-model="viewDialogVisible"
        @close="viewDialogVisible = false" top="84px" center>
        <template #title>
          <div class="dialog-header">{{ t('dashboard.dialog.title.text_2') }}</div>
        </template>
        <div class="dialog-body">
          <el-form label-width="160px" label-position="right">
            <el-row>
              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_11')">
                  <div :class="['bank-auth-status', `bank-auth-status-${bankList[0].bankAcctCheckStatus}`]">
                    {{ bankAuthStatusEnum[bankList[0].bankAcctCheckStatus] }}
                  </div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_1')">
                  <div>{{ bankList[0].bankCtryCode }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_2')">
                  <div>{{ bankAcctTypeEnum[bankList[0].bankAcctType] }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_3')">
                  <div>{{ bankList[0].bankAcctName }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_12')">
                  <div>{{ bankList[0].cur || '-' }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_5')">
                  <div>{{ bankList[0].bankAcctNo }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_7')" v-if="bankList[0].bankAcctType === '2'">
                  <div>{{ bankList[0].cardId }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_8')" v-if="bankList[0].bankAcctType === '1'">
                  <div>{{ bankList[0].bankName }}</div>
                </el-form-item>
              </el-col>

              <el-col>
                <el-form-item :label="t('dashboard.dialog.label.text_9')" v-if="bankList[0].bankAcctType === '1'">
                  <div>{{ bankList[0].bankProvince + '/' + bankList[0].bankCity }}</div>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </div>
        <template #footer>
          <div class="dialog-footer">
            <el-button type="primary" @click="viewDialogVisible = false">{{ t('dashboard.dialog.content.text_5')
              }}</el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<style lang="scss" scoped rel="stylesheet/scss">
.personal-container {
  width: 100%;
  box-sizing: border-box;

  .content-wrap {
    background-color: #fff;
    padding: 16px 22px 20px;
    // margin-bottom: 10px;

    .title-wrap {
      font-size: 14px;
      border-left: 4px solid #00acea;
      padding-left: 10px;
      margin-bottom: 8px;

    }

    .subtitle-wrap {
      color: #999;
      font-size: 14px;
      padding-left: 14px;

    }

    .form-item-wrap {
      padding: 30px 26px;
      margin-top: 10px;
      background: #F8F8F8;

      .password-wrap {
        width: 100%;
        box-sizing: border-box;
        position: relative;

        .icon {
          font-size: 15px;
          position: absolute;
          top: 50%;
          right: 20px;
          transform: translateY(-50%);
        }
      }

      .confirm {
        width: fit-content;
        height: 40px;
        padding: 0 20px;
        color: #fff;
        border-radius: 5px;
        margin-left: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-image: linear-gradient(270deg, #6095F7 0%, #8CC1F9 100%);
        cursor: pointer;
      }
    }
  }
}

:deep(.el-form-item__content) {
  flex: inherit !important;
  width: auto !important;

  .el-input__inner,
  .el-textarea__inner {
    width: 360px !important;
    border-radius: 4px !important;
  }

  .el-input {
    width: auto;
  }
}

// 银行卡管理相关样式
.df-jc-sb {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.a {
  color: #6095F7;
  cursor: pointer;
  display: inline-block;
  margin-left: 5px;

  &:hover {
    text-decoration: underline;
  }

  &.gray {
    color: #999;
    cursor: not-allowed;

    &:hover {
      text-decoration: none;
    }
  }
}

.red {
  color: #e85f5f;
  margin-left: 10px;
}

// 银行卡信息展示样式 - 参考operation-control实现
.card-info-wrap {
  width: 270px;
  height: 180px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  background: linear-gradient(166.14deg, #C8E7FF 0.44%, #E6FFFC 90.11%);
  position: relative;
  border-radius: 12px;


  .card-status {
    font-size: 13px;
    height: fit-content;
    box-sizing: border-box;
    letter-spacing: 0;
    text-align: center;
    border-radius: 4px;
    padding: 0 1px;
    position: absolute;
    right: 0;
    top: 0;
    background: #ffffff9e;
    padding: 2px 5px;
    display: flex;
    align-items: center;
  }

  .card-status-CF {
    color: #229C75;
    // border: 1px solid #229C75;
  }

  .card-status-RJ,
  .card-status-ST {
    color: #E85F5F;
    // border: 1px solid #E85F5F;
  }

  .card-status-CP,
  .card-status-WC,
  .card-status-RC {
    color: #FEA801;
    // border: 1px solid #FEA801;
  }

  .card-h1 {
    display: flex;
    align-items: center;
    font-size: 22px;
  }

  .card-h2 {
    display: flex;
    align-items: center;
    font-size: 16px;
  }

}

.bg-render {
  width: 100%;
  position: relative;

  &>img {
    width: 100%;
    display: block;
    height: auto;
  }
}

.add-card-container {
  .bank-auth-status {
    height: 31px;
    border-radius: 3px;
    font-size: 14px;
    letter-spacing: -1px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 5px 11px;
  }

  .bank-auth-status-CF {
    color: #00acea;
    background-color: #eef9fd;
  }

  .bank-auth-status-RJ,
  .bank-auth-status-ST {
    color: #e85f5f;
    background-color: #fdf4f4;
  }

  .bank-auth-status-CP,
  .bank-auth-status-WC,
  .bank-auth-status-RC {
    color: #fea801;
    background-color: #fff9ee;
  }

  :deep(.el-form-item__content) {
    flex: inherit !important;
    width: auto !important;

    .el-select .el-input__wrapper {
      width: 320px !important;
    }

    .el-input .el-input__wrapper {
      width: 320px !important;
    }


    .el-input {
      width: auto;
    }
  }
}
</style>

<style lang="scss" rel="stylesheet/scss">
.reset-dialog-container {
  .dialog-body {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .dialog-topic {
    background-color: #fffcf5;
    padding: 14px 31px;
    border-radius: 4px;
    margin-bottom: 20px;

    .dialog-topic-item {
      font-size: 13px;
      font-family: PingFangSC-Regular, PingFang SC, serif;
      font-weight: 400;
      color: #eb753c;
      line-height: 22px;
    }
  }

  .el-dialog__header {
    margin: 0;
    padding: 0;
    border-radius: 3px;
    overflow: hidden;
  }

  .dialog-header {
    height: 70px;
    padding-left: 35px;
    background-color: #f5fcfe;
    display: flex;
    align-items: center;
  }

  .dialog-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  :deep(.el-form-item__label) {
    padding: 0 30px 0 0 !important;
  }

  :deep(.el-form-item__content) {
    flex: inherit !important;
    width: auto !important;

    .el-input__inner,
    .el-textarea__inner {
      width: 288px !important;
      border-radius: 4px !important;
    }

    .el-select,
    .el-cascader {

      .el-input__inner,
      .el-textarea__inner {
        width: 268px !important;
        border-radius: 4px !important;
      }
    }

    .el-input {
      width: auto;
    }
  }
}

.add-bank-card {
  width: 320px;
  height: 180px;
  background: #ffffff;
  color: #409EFF;
  background: #FBFBFB;
  border: dashed 1px #D9D9D9;
  border-radius: 12px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }
}
</style>
